import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    /** Which body grid + left-rail variant the shell renders. */
    shell?: 'editor' | 'library'
    /** Show the right-hand Insights rail. */
    rightRail?: boolean
    /** Bottom tools-bar variant (omit to hide it). */
    tools?: 'editor' | 'library'
  }
}

// Account-only: a global guard (below) bounces anonymous visitors to /login, so
// every user carries a session (and entitlements load reliably). Everything else
// renders inside the one persistent desktop AppShell; lazy-loaded components.
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'library',
        component: () => import('@/features/library/LibraryPage.vue'),
        meta: { shell: 'library', tools: 'library' },
      },
      {
        path: 'book/new',
        name: 'book-new',
        component: () => import('@/features/library/CreateBookPage.vue'),
        meta: { shell: 'library' },
      },
      {
        path: 'book/:id',
        name: 'book-editor',
        component: () => import('@/features/editor/BookEditorPage.vue'),
        props: true,
        meta: { shell: 'editor', tools: 'editor' },
      },
      {
        path: 'book/:id/page/:pageId',
        name: 'page-editor',
        component: () => import('@/features/editor/PageEditorPage.vue'),
        props: true,
        meta: { shell: 'editor', tools: 'editor', rightRail: true },
      },
      {
        path: 'book/:id/settings',
        name: 'book-settings',
        component: () => import('@/features/settings/BookSettingsPage.vue'),
        props: true,
        meta: { shell: 'editor' },
      },
      {
        path: 'book/:id/preview',
        name: 'book-preview',
        component: () => import('@/features/preview/BookPreviewPage.vue'),
        props: true,
        meta: { shell: 'editor' },
      },
    ],
  },
  {
    // Standalone (outside the AppShell) — email/password sign in / sign up.
    // Guest-first: no route guard, signing in is optional.
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/AuthPage.vue'),
  },
  {
    // Standalone paywall — client gates route here (optionally `?r=<reason>`).
    path: '/upgrade',
    name: 'upgrade',
    component: () => import('@/features/billing/UpgradePage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/NotFoundPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Account-only gate: an anonymous visitor (no session token) can only reach the
// sign-in page. `isAuthenticated` reflects the persisted token synchronously, so
// a returning user passes straight through; `redirect` sends them back after
// signing in.
router.beforeEach((to) => {
  const auth = useAuthStore()
  if (to.name !== 'login' && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  return true
})

export default router
